import { useEffect, useRef, useState } from "react";
import { Alert, Button, Image, Space, Spin, Typography, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { apiUrl } from "@/consts";
import { TOKEN_KEY } from "@/authProvider";

type Props = { value?: string | null; onChange?: (key: string | null) => void; onBusyChange: (busy: boolean) => void };
export function EventCardTemplate({ value, onChange, onBusyChange }: Props) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string>();
  const [error, setError] = useState<string>();
  const sequence = useRef(0);
  const lastPreviewKey = useRef<string | null | undefined>(undefined);
  const headers = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
  async function renderTemplate(key?: string | null, file?: File) {
    const request = ++sequence.current;
    setBusy(true); onBusyChange(true); setError(undefined);
    try {
      let fileId: string | undefined;
      if (file) {
        if (file.size > 12 * 1024 * 1024) throw new Error("SVG must be at most 12 MB");
        const response = await fetch(`${import.meta.env.VITE_API_URL}/file/presigned_url`, {
          method: "POST", headers: headers(), body: JSON.stringify({ fileCount: 1, mimeType: "IMAGE", contentType: "image/svg+xml", uploadType: "CARD_TEMPLATE" }),
        });
        if (!response.ok) throw new Error("Unable to start upload");
        const upload = (await response.json())?.data?.[0];
        if (!upload?.fileID || !upload?.url || !upload?.fields) throw new Error("Unable to start upload");
        const body = new FormData();
        Object.entries(upload.fields).forEach(([name, value]) => body.append(name, String(value)));
        body.append("Content-Type", "image/svg+xml"); body.append("file", file);
        if (!(await fetch(upload.url, { method: "POST", body })).ok) throw new Error("SVG upload failed");
        fileId = upload.fileID;
      }
      const response = await fetch(`${apiUrl}/scouting-event/player-card-template/preview`, {
        method: "POST", headers: headers(), body: JSON.stringify({ key, fileId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Unable to preview template");
      if (request !== sequence.current) return;
      setPreview(result.preview); lastPreviewKey.current = result.key;
      if (file) onChange?.(result.key);
    } catch (e) {
      if (request === sequence.current) {
        const text = e instanceof Error ? e.message : "Unable to preview template";
        setError(text); message.error(text);
      }
    } finally {
      if (request === sequence.current) { setBusy(false); onBusyChange(false); }
    }
  }
  useEffect(() => {
    if (!value) {
      sequence.current++;
      lastPreviewKey.current = null;
      setPreview(undefined);
      setError(undefined);
      setBusy(false);
      onBusyChange(false);
    } else if (lastPreviewKey.current !== value) {
      void renderTemplate(value);
    }
  }, [value]);
  function clearTemplate() {
    sequence.current++;
    lastPreviewKey.current = null;
    setPreview(undefined);
    setError(undefined);
    setBusy(false);
    onBusyChange(false);
    onChange?.(null);
  }
  return <Space direction="vertical" size="middle" style={{ width: "100%" }}>
    <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
      Upload SVG artwork for this event with player, photo, flag and six stat placeholders. Embed artwork images as PNG/JPEG; convert custom lettering to paths.
    </Typography.Paragraph>
    <Space wrap>
      <Upload accept=".svg,image/svg+xml" showUploadList={false} disabled={busy} beforeUpload={file => { void renderTemplate(undefined, file); return false; }}>
        <Button icon={<UploadOutlined />} disabled={busy}>Upload SVG</Button>
      </Upload>
      {value && <Button disabled={busy} onClick={clearTemplate}>Remove template</Button>}
      {error && value && <Button disabled={busy} onClick={() => void renderTemplate(value)}>Retry preview</Button>}
    </Space>
    {error && <Alert type="error" message={error} description={value ? "The selected template is unchanged. Upload a corrected SVG or retry the preview." : "Upload a corrected SVG."} />}
    <Spin spinning={busy}>
      {preview && <Image src={preview} alt="Dummy player card for Alex Murphy" width={250} />}
    </Spin>
    <Typography.Text type="secondary">{value ? "Event template selected. Save the event to apply. Existing issued cards are unchanged until regenerated." : "No player card template selected. Upload an SVG to set one for this event."}</Typography.Text>
  </Space>;
}
